import * as Sentry from "@sentry/browser";
import { LogicEngine } from "json-logic-engine";
import type { RulesLogic } from "json-logic-js";
import type { EmissionRecordView } from "../../apiClient/generated";

const engine = new LogicEngine();

// JSON Logic compares values as-is, so comparing datetimes would rely on ISO
// string ordering (fragile across offsets/precision). These ops normalize both
// sides to epoch ms, letting rules compare dates without pre-converting the
// context objects.
const toTime = (value: unknown): number =>
    new Date(value as string | number | Date).getTime();

engine.addMethod(
    "dateGt",
    ([a, b]: [unknown, unknown]) => toTime(a) > toTime(b),
);
engine.addMethod(
    "dateLt",
    ([a, b]: [unknown, unknown]) => toTime(a) < toTime(b),
);
engine.addMethod("dateToEpoch", ([value]: [unknown]) => toTime(value));
engine.addMethod("truncNumber", ([value, num_digits]: [unknown, number]) => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return Number(value.toFixed(num_digits));
    }
});

// The single object every rule is evaluated against. Passed whole (not plucked)
// so new rules can reach any path without changing the context builder:
//   {var: "values.formData.myField"}, {var: "emissionRecord.dataPoint.detectionTimestamp"}
export interface Context {
    values: Record<string, unknown>;
    emissionRecord: EmissionRecordView;
    extra: {
        currentlyOpenSite?: string;
    };
}

// An absent or empty ({}) rule means "no rule configured". We treat it as such
// explicitly because JSON Logic would otherwise evaluate {} to a truthy object.
const isEmptyRule = (rule: RulesLogic | null | undefined): boolean =>
    rule === null ||
    rule === undefined ||
    (typeof rule === "object" &&
        !Array.isArray(rule) &&
        Object.keys(rule).length === 0);

// Core evaluator. Returns whatever the rule evaluates to: a boolean for
// conditions, a value for suggestions. The engine's loose typing is confined to
// the single cast here so callers always see RulesLogic / Context.
const evaluate = (rule: RulesLogic, context: Context): unknown =>
    engine.run(rule as object, context as unknown as object);

// Conditions (validation / visibility): coerce the result to a boolean.
// No rule -> true, i.e. don't hide the field and don't block submission.
export const evaluateCondition = (
    rule: RulesLogic | null | undefined,
    context: Context,
): boolean => {
    if (isEmptyRule(rule)) return false;
    // A malformed rule must never break the form. Fail closed: treat an
    // errored condition as false (don't hide the field, don't block submission).
    try {
        return Boolean(evaluate(rule as RulesLogic, context));
    } catch (error) {
        Sentry.captureException(error);
        return false;
    }
};

// A field's suggestion_rule is stored as an envelope so metadata can live
// alongside the JSON Logic rule. JSON Logic itself has no room for extra keys
// (an operation object must have exactly one key, which is the operator), so we
// wrap it: the engine only ever sees `logic`, while `autofill` and `comment`
// are ours to interpret.
export interface SuggestionConfig {
    logic?: RulesLogic;
    // When true, an empty field is populated with the suggested value once, when
    // the record first loads. After that the value behaves as a manual suggestion.
    autofill?: boolean;
    // When true, the form will always suggest the value, even if
    // the field is already populated.
    alwaysSuggest?: boolean;
    comment?: string;
}

export interface SuggestionResult {
    value: unknown;
    autofill: boolean;
}

// Suggestion engine: return the computed value (and its autofill flag), or null
// to mean "no suggestion". A missing/empty rule abstains, as does a rule that
// resolves to null/undefined.
export const evaluateSuggestion = (
    config: SuggestionConfig | null | undefined,
    context: Context,
): SuggestionResult | null => {
    if (!config || isEmptyRule(config.logic)) return null;
    // A malformed rule must never break the form; abstain from suggesting.
    try {
        const value = evaluate(config.logic as RulesLogic, context) ?? null;
        if (value === null) return null;
        return { value, autofill: config.autofill ?? false };
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

export interface VisibilityConfig {
    logic?: RulesLogic;
    comment?: string;
}

export const evaluateVisibility = (
    config: VisibilityConfig | null | undefined,
    context: Context,
): boolean => {
    // No visibility rule means always displayed
    if (!config || isEmptyRule(config.logic)) return true;
    try {
        return Boolean(evaluate(config.logic as RulesLogic, context));
    } catch (error) {
        Sentry.captureException(error);
        return true;
    }
};
